import React, { useState, useEffect } from "react";
import { Page } from "../../components/Page";
import { getCurrentUser, getRoomType, deleteRooms } from "../../api-config/config";
import NewAddRoomForm from "../../components/Rooms/Rooms";
import SimpleTable from "../../components/table/SimpleTable";
import LoaderWrapper from "../../components/LoaderWrapper/LoaderWrapper";

function RoomListGrid({ row, roomAdded, showSnackbarWithCallback }: any) {
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [roomData, setRoomData] = useState<any>({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableRows, setTableRows] = useState<any[]>([]);

  useEffect(() => {
    if (!row) return;

    const formatted = row.map((item: any, index: number) => ({
      serial: index + 1,
      room_id: item.id,
      room: item.room_name,
      room_type_id: item.room_type_id,
      roomtype: item.room_type_name,
      floor_id: item.floor_id,
      floor: item.floor_name,
      block_id: item.block_id,
      block: item.block_name,
      hotel_code: item.hotel_code,
      is_active: Number(item.is_active ?? 1),
    }));

    setTableRows(formatted);
  }, [row]);

  const onEditClick = (r: any) => {
    if (!r.is_active) return;
    setRoomData(r);
    setOpen(true);
  };

  const onToggleClick = (r: any) => {
    setSelectedRoom(r);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRoom) return;

    const nextActive = selectedRoom.is_active ? 0 : 1;

    try {
      await deleteRooms({
        id: selectedRoom.room_id,
        hotel_code: selectedRoom.hotel_code,
        is_active: String(nextActive),
        is_deleted: String(nextActive ? 0 : 1),
      });

      setTableRows((prev) =>
        prev.map((row) =>
          row.room_id === selectedRoom.room_id
            ? { ...row, is_active: nextActive }
            : row
        )
      );

      if (showSnackbarWithCallback) {
        showSnackbarWithCallback(
          nextActive
            ? `Room "${selectedRoom.room}" activated`
            : `Room "${selectedRoom.room}" deactivated`,
          "success",
          () => {
            roomAdded();
          }
        );
      }
    } catch {
      if (showSnackbarWithCallback) {
        showSnackbarWithCallback("Failed to update room status", "error");
      }
    } finally {
      setDeleteOpen(false);
      setSelectedRoom(null);
    }
  };

  const paginated = tableRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns = [
    { header: "#", accessor: "serial", width: "5%" },
    { header: "Room", accessor: "room", width: "20%" },
    { header: "Room Type", accessor: "roomtype", width: "20%" },
    { header: "Floor", accessor: "floor", width: "15%" },
    { header: "Block", accessor: "block", width: "15%" },
    {
      header: "Status",
      accessor: "is_active",
      width: "10%",
      align: "center" as const,
      render: (_: any, r: any) => (
        <div
          onClick={() => onToggleClick(r)}
          style={{
            width: 44,
            height: 22,
            borderRadius: 20,
            background: r.is_active ? "#22c55e" : "#cf4235",
            cursor: "pointer",
            position: "relative",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: "#fff",
              position: "absolute",
              top: 2,
              left: r.is_active ? 24 : 2,
              transition: "0.2s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
            }}
          />
        </div>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      width: "10%",
      align: "center" as const,
      render: (_: any, r: any) => (
        <span
          onClick={() => r.is_active && onEditClick(r)}
          style={{
            cursor: r.is_active ? "pointer" : "not-allowed",
            opacity: r.is_active ? 1 : 0.3,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
          </svg>
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="roomtype-header">
        <h2 className="roomtype-title">Rooms List</h2>
        <div className="roomtype-btns">
          <button
            className="my-custom-btn"
            onClick={() => {
              setRoomData({});
              setOpen(true);
            }}
          >
            Add Room
          </button>
        </div>
      </div>

      <SimpleTable columns={columns} data={paginated} />

      <div style={{ display: "flex", justifyContent: "space-between", padding: 12 }}>
        <div>
          Show{" "}
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(+e.target.value)}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span>Page {page + 1}</span>
          <button
            disabled={(page + 1) * rowsPerPage >= tableRows.length}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <NewAddRoomForm
                handleClose={() => setOpen(false)}
                onRoomAdded={roomAdded}
                roomData={roomData}
                showSnackbarWithCallback={showSnackbarWithCallback}
              />
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="delete-modal-overlay" onClick={() => setDeleteOpen(false)} >
          <div className="delete-modal-box" onClick={(e) => e.stopPropagation()} >
            <h3 className="delete-modal-title">
              Are you sure you want to proceed?
            </h3>

            <p className="delete-modal-desc">
              Room <span>{selectedRoom?.room}</span> will be{" "}
              {selectedRoom?.is_active ? "deactivated" : "activated"}.
            </p>

            <div className="delete-modal-actions">
              <button className="delete-btn-cancel" onClick={() => setDeleteOpen(false)} >
                No
              </button>
              <button className="delete-btn-confirm" onClick={handleDeleteConfirm} >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NewRoomList() {
  const [roomList, setRoomList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loggedUser = getCurrentUser();

  // Snackbar state in parent
  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: "",
    type: "success" as "success" | "error",
  });

  const showSnackbarWithCallback = (msg: string, type: "success" | "error", callback?: () => void) => {
    setSnackbar({ visible: true, message: msg, type });
    if (callback) {
      callback();
    }
    
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const loadRooms = (showLoader = true) => {
    if (showLoader) {
      setLoading(true);
    }
    setError(null);

    getRoomType(loggedUser?.hotel_code)
      .then((res: any) => {
        if (res?.status_message === "success") {
          const rooms: any[] = [];
          res.data.forEach((rt: any) => {
            rt.rooms.forEach((r: any) => {
              r.room_type_name = rt.room_type_name;
            });
            rooms.push(...rt.rooms);
          });
          setRoomList(rooms);
        }
      })
      .catch((err: any) => {
        setError(err.message || "Failed to load rooms");
      })
      .finally(() => {
        setLoading(false);
        setInitialLoad(false);
      });
  };

  useEffect(() => {
    loadRooms();
  }, []);

  return (
    <Page title="Home">
      <div style={{ padding: "0 20px", width: "100%" }}>
        {loading && initialLoad ? (
          <LoaderWrapper />
        ) : error ? (
          <div style={{ color: "red", padding: 24 }}>{error}</div>
        ) : (
          <RoomListGrid 
            row={roomList} 
            roomAdded={() => loadRooms(false)}
            showSnackbarWithCallback={showSnackbarWithCallback}
          />
        )}
        
        <div id="snackbar" className={`${snackbar.visible ? "show" : ""} ${snackbar.type}`}>
          {snackbar.message}
        </div>
      </div>
    </Page>
  );
}